import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useAuthentication } from 'authentication/context'
import { HomeScreenProps } from 'home/stack/types'
import { HomeView } from 'home/views/home'
import moment from 'moment'
import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { LogoHeader } from 'shared/components/logo_header'
import { useStoredState } from 'shared/hooks/stored_state'
import { QueryErrorsIconModal } from 'shared/query_errors_icon_modal'
import { useUser } from 'users/queries/detail'
import { useUpdateUserPhoto } from 'users/queries/update_photo'
import { useRoutesMap } from './routes'
import UploadProfilePhotoModal from './upload_profile_photo_modal'
import { useHomeTilesData } from './utils'
import { PendingView } from './views/pending'

interface WelcomeMessage {
  index: number
  timestamp: number
}

export function HomeScreen({ navigation }: HomeScreenProps) {
  const welcomeTextList = t('welcomeTextList')
  const routesMap = useRoutesMap()
  const { logout, profileNeedsReview } = useAuthentication()

  const { data: user } = useUser()

  const updateAvatarMutation = useUpdateUserPhoto()

  const { handleNavigation } = useRoutingContext()

  const [welcomeMessage, setWelcomeMessage] =
    useStoredState<WelcomeMessage>(`welcome_message`)

  const onAvatarChange = useCallback(
    async (avatarUrl: string, mimeType: string) => {
      await updateAvatarMutation.mutateAsync({ photoUrl: avatarUrl, mimeType })
    },
    [updateAvatarMutation]
  )

  const getRandomWelcomeMessageIndex = useCallback(() => {
    return Math.floor(Math.random() * Math.floor(welcomeTextList.length))
  }, [welcomeTextList.length])

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <LogoHeader />,
    })
  }, [navigation])

  useEffect(() => {
    if (user) {
      if (profileNeedsReview) {
        handleNavigation({
          routeName: 'settings',
          routeParams: { type: 'store' },
        })
      }
    }
  }, [handleNavigation, profileNeedsReview, user])

  useEffect(() => {
    const refreshDate = moment(welcomeMessage?.timestamp ?? 0)
    const currentDate = moment()

    if (refreshDate.isBefore(currentDate, 'day')) {
      setWelcomeMessage({
        index: getRandomWelcomeMessageIndex(),
        timestamp: currentDate.valueOf(),
      })
    }
  }, [
    getRandomWelcomeMessageIndex,
    setWelcomeMessage,
    welcomeMessage?.timestamp,
  ])

  const homeTilesData = useHomeTilesData()

  // useEffect(() => {
  //   if (!isLoading) {
  //     try {
  //       SplashScreen.hideAsync().catch(null)
  //     } catch {
  //       // ignore "no native splash screen registered for given view controller" error
  //     }
  //   }
  // }, [isLoading])

  return (
    <>
      {user?.role ? (
        <HomeView
          routes={routesMap}
          items={homeTilesData}
          onButtonPress={(route, params) =>
            handleNavigation({ routeName: route.name, routeParams: params })
          }
          user={user}
          welcomeText={welcomeTextList[welcomeMessage?.index ?? 0]}
        />
      ) : (
        <PendingView
          onLogout={async () => {
            if (logout && user?.id) {
              await logout(user.id)
            }
          }}
        />
      )}
      <UploadProfilePhotoModal onAvatarChange={onAvatarChange} />
      <QueryErrorsIconModal />
    </>
  )
}
